import React, { useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { createOpportunity, updateOpportunity } from '../services/api';
import { useOpportunityStore, type Opportunity } from '../stores/opportunityStore';
import { useStageStore } from '../stores/stageStore';
import { useCustomerStore } from '../stores/customerStore';
import { Input, Textarea, CustomSelect, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import { useTranslation } from 'react-i18next';
import { canEditArchivedCustomerData } from '../utils/customerUtils';



interface OpportunityDialogProps {
  isOpen: boolean;
  /** 樂觀更新：送出後立即關閉（API 尚未完成）；需要重抓資料請用 useDataChanged */
  onClose: () => void;
  opportunity?: Opportunity; // 型別與 opportunityStore 同一份定義
  preselectedCustomerId?: string;
}

interface FormData {
  customerId: string;
  title: string;
  description?: string;
  value: number;
  currentStageId: string;
}

const OpportunityDialog: React.FC<OpportunityDialogProps> = ({
  isOpen,
  onClose,
  opportunity,
  preselectedCustomerId,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, setValue, getValues, reset, watch } = useForm<FormData>();
  const { allCustomers, isLoadingAllCustomers, fetchAllCustomers } = useCustomerStore();
  // 階段用共用 store（有快取直接顯示、背景更新）；原本每次開啟都自己抓，階段下拉每次都先閃「載入中」
  const { stages, loading: stagesLoading, fetchStages } = useStageStore();

  const { updateOpportunity: updateOpportunityInStore, addOpportunity: addOpportunityToStore, replaceOpportunity, removeOpportunity } = useOpportunityStore();

  const isEditMode = !!opportunity;
  
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(opportunity?.customer);
  
  // 監聽表單值變化
  const watchedCustomerId = watch('customerId', '');
  const watchedStageId = watch('currentStageId', '');

  // 開啟時背景更新客戶、階段下拉
  useEffect(() => {
    if (isOpen) {
      fetchAllCustomers();
      fetchStages();
    }
  }, [isOpen, fetchAllCustomers, fetchStages]);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (opportunity) {
        // 編輯模式
        setValue('customerId', opportunity.customerId);
        setValue('title', opportunity.title);
        setValue('description', opportunity.description || '');
        setValue('value', opportunity.value);
        setValue('currentStageId', opportunity.currentStageId);
      } else {
        // 新增模式
        reset();
        if (preselectedCustomerId) {
          setValue('customerId', preselectedCustomerId);
        }
      }
    }
  }, [isOpen, opportunity, preselectedCustomerId, setValue, reset]);

  // 新增時預設第一個階段：階段可能在開啟後才載入或背景更新，只在還沒選階段時補上
  // （不能放進上面的初始值：stages 一更新就會重跑 reset()，把使用者已輸入的內容清掉）
  useEffect(() => {
    if (!isOpen || opportunity || stages.length === 0 || getValues('currentStageId')) return;
    const firstStage = [...stages].sort((a, b) => a.orderIndex - b.orderIndex)[0];
    setValue('currentStageId', firstStage.id);
  }, [isOpen, opportunity, stages, getValues, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }

    const submitData = {
      customerId: data.customerId,
      title: data.title.trim(),
      description: data.description?.trim(),
      value: Number(data.value),
      currentStageId: data.currentStageId,
    };

    const selectedCustomer = allCustomers.find(c => c.id === data.customerId);
    const selectedStage = stages.find(s => s.id === data.currentStageId);

    if (isEditMode) {
      // UPDATE：保存原始值以供回滾
      const originalOpportunity = { ...opportunity };

      // 樂觀更新 + 立即關閉
      updateOpportunityInStore(opportunity.id, {
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim(),
        value: Number(data.value),
        currentStageId: data.currentStageId,
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          company: selectedCustomer.company
        } : opportunity.customer,
        currentStage: selectedStage ?? opportunity.currentStage
      });
      handleClose();

      // 背景執行 API 更新
      updateOpportunity(opportunity.id, submitData).then(() => {
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('opportunities'); // API 成功後才通知：漏斗、統計、看板、儀表板重新載入
      }).catch((error: any) => {
        console.error('商機更新失敗:', error);
        // 回滾到原始值
        updateOpportunityInStore(opportunity.id, originalOpportunity);
        toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
      });
    } else {
      // CREATE：臨時 id 樂觀新增
      const tempId = `temp-${Date.now()}`;
      const optimisticOpportunity = {
        id: tempId,
        customerId: data.customerId,
        title: data.title.trim(),
        description: data.description?.trim(),
        value: Number(data.value),
        currentStageId: data.currentStageId,
        organizationId: '',
        userId: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          company: selectedCustomer.company
        } : { id: data.customerId, name: '', email: '', company: '' },
        currentStage: selectedStage ? {
          id: selectedStage.id,
          name: selectedStage.name,
          probability: selectedStage.probability,
          orderIndex: selectedStage.orderIndex,
          organizationId: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } : { id: data.currentStageId, name: '', probability: 0, orderIndex: 0, organizationId: '', createdAt: '', updatedAt: '' },
      };

      addOpportunityToStore(optimisticOpportunity as any);
      handleClose();

      createOpportunity(submitData).then((response) => {
        replaceOpportunity(tempId, response.data);
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('opportunities'); // API 成功後才通知：漏斗、統計、看板、儀表板重新載入
      }).catch((error: any) => {
        console.error('商機建立失敗:', error);
        removeOpportunity(tempId);
        toast.error(error.response?.data?.error || t('common.messages.createFailed'));
      });
    }
  };

  if (!isOpen) return null;

  return (
    <DialogShell onClose={handleClose} className="max-w-2xl">
      <DialogHeader title={isEditMode ? t('opportunities.edit.title', { ns: 'pages' }) : t('opportunities.add.title', { ns: 'pages' })} onClose={handleClose} closeLabel={t('common.actions.close')} />

      {/* 表單內容 */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* 封存客戶警告提示 */}
          {isEditMode && !canEdit && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('common.warnings.archivedCustomerData')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <FormFieldWithError
            label={t('common.forms.labels.customer')}
            required={true}
            error={errors.customerId?.message}
            htmlFor="customerId"
          >
            <CustomSelect
              id="customerId"
              {...register('customerId', { required: t('common.forms.validation.required') })}
              value={watchedCustomerId}
              onChange={(value) => setValue('customerId', value)}
              variant={errors.customerId ? "error" : "default"}
              disabled={isLoadingAllCustomers}
              placeholder={t('common.forms.placeholders.selectCustomer')}
              options={[
                ...(isLoadingAllCustomers ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...allCustomers
                  .filter(customer => customer.archived !== true)
                  .sort((a, b) => new Date(b.createdAt || b.updatedAt || '').getTime() - new Date(a.createdAt || a.updatedAt || '').getTime())
                  .map(customer => ({ 
                    value: customer.id, 
                    label: `${customer.name}${customer.company ? ` (${customer.company})` : ''}` 
                  }))
              ]}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('opportunities.form.title', { ns: 'pages' })}
            required={true}
            error={errors.title?.message}
            htmlFor="title"
          >
            <Input
              type="text"
              id="title"
              {...register('title', { 
                required: t('common.forms.validation.required'),
                maxLength: { value: 200, message: t('common.forms.validation.maxLength', { length: 200 }) }
              })}
              variant={errors.title ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('opportunities.form.value', { ns: 'pages' })}
            required={true}
            error={errors.value?.message}
            htmlFor="value"
          >
            <Input
              type="text"
              id="value"
              {...register('value', { 
                required: t('common.forms.validation.required'),
                pattern: {
                  value: /^\d+(\.\d{0,2})?$/,
                  message: t('common.forms.validation.invalidAmount')
                }
              })}
              variant={errors.value ? "error" : "default"}
              placeholder="0"
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('opportunities.form.stage', { ns: 'pages' })}
            required={true}
            error={errors.currentStageId?.message}
            htmlFor="currentStageId"
          >
            <CustomSelect
              id="currentStageId"
              {...register('currentStageId', { required: t('common.forms.validation.required') })}
              value={watchedStageId}
              onChange={(value) => setValue('currentStageId', value)}
              variant={errors.currentStageId ? "error" : "default"}
              disabled={stagesLoading}
              placeholder={t('common.forms.placeholders.selectStage')}
              options={[
                ...(stagesLoading ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...stages
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map(stage => ({ 
                    value: stage.id, 
                    label: `${stage.name} (${Math.round(stage.probability * 100)}%)` 
                  }))
              ]}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('opportunities.form.description', { ns: 'pages' })}
            error={errors.description?.message}
            htmlFor="description"
          >
            <Textarea
              id="description"
              rows={3}
              {...register('description', {
                maxLength: { value: 1000, message: t('common.forms.validation.maxLength', { length: 1000 }) }
              })}
              variant={errors.description ? "error" : "default"}
            />
          </FormFieldWithError>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            isEditMode={isEditMode}
            disabled={!canEdit}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default OpportunityDialog;